// Copyright (c) 2023 Jürgen Gebath
//
// This software is released under the MIT License.
// https://opensource.org/licenses/MIT

import * as React from "react";
import {
  AppBar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Container,
  Tooltip,
  Grid,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { NavLink } from "react-router-dom";

import { useTheme } from "@mui/material/styles";
import { ColorModeContext } from "../index";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlineIcon from "@mui/icons-material/LightModeOutlined";

import { styled } from "@mui/material/styles";

const pages = [
  { name: "Home", route: "/" },
  { name: "Aktivitäten", route: "/Aktivitaeten" },
  { name: "Mannschaft", route: "/Mannschaft" },
  { name: "Geschichte", route: "/Geschichte" },
  // { name: "Ausrüstung", route: "/Ausruestung" },
  // { name: "Information", route: "/Information" },
  { name: "Rechtliches", route: "/Rechtliches" },
];

const Header = React.forwardRef((props, ref) => {
  const [anchorElNav, setAnchorElNav] = React.useState(null);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleColorMode = () => {
    colorMode.toggleColorMode();
    handleCloseNavMenu();
  };

  const theme = useTheme();
  const colorMode = React.useContext(ColorModeContext);

  function colorModeButton() {
    return (
      <IconButton
        id="btnColorMode"
        sx={{ ml: 1 }}
        onClick={handleColorMode}
        color="inherit">
        {theme.palette.mode === "dark" ? (
          <LightModeOutlineIcon
            sx={(theme) => ({
              color: theme.palette.text.brand,
            })}
          />
        ) : (
          <DarkModeOutlinedIcon
            sx={(theme) => ({
              color: theme.palette.text.brand,
            })}
          />
        )}
      </IconButton>
    );
  }

  const StyledNavButton = styled((props) => <Button {...props} />)(
    ({ theme }) => ({
      color: theme.palette.text.brand,
      backgroundColor: theme.palette.brand.main,
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
      },
      "&.active": {
        backgroundColor: theme.palette.primary.main,
      },
    })
  );

  const StyledMenu = styled((props) => <Menu {...props} />)(({ theme }) => ({
    ".MuiPaper-root": {
      color: theme.palette.text.brand,
      backgroundColor: theme.palette.brand.main,
    },
  }));

  return (
    <AppBar sx={{ paddingTop: 1 }}>
      <Container>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center">
          {/* Logo vom niederösterreichischen Landesfeuerverband */}
          <Grid
            item
            xs={1}>
            <Box
              component="img"
              src={process.env.PUBLIC_URL + "/logo_noe_lfv.svg"}
              alt="Logo vom niederösterreichischen Landesfeuerverband"
            />
          </Grid>
          {/* Logo der Freiwilligen Feuerwehr Großrust-Merking + navigation big screen */}
          <Grid
            container
            item
            xs={10}
            sm={10}
            md={11}>
            <Grid item>
              <Box
                component="img"
                src={process.env.PUBLIC_URL + "/logo_ff_grossrust_merking.svg"}
                alt="Logo der Freiwilligen Feuerwehr Großrust-Merking"
                sx={(theme) => ({
                  maxWidth: "100%",
                })}
              />
            </Grid>
            {/* navigation big screens */}
            <Grid item>
              <Box
                sx={(theme) => ({
                  display: "none",
                  [theme.breakpoints.up("md")]: {
                    display: "block",
                    marginLeft: "5px",
                  },
                  [theme.breakpoints.up("lg")]: {
                    display: "block",
                    marginLeft: "10px",
                  },
                })}>
                {pages.map((page) => (
                  <StyledNavButton
                    key={page.name}
                    component={NavLink}
                    to={page.route}
                    onClick={() => {}}>
                    {page.name}
                  </StyledNavButton>
                ))}
                {colorModeButton()}
              </Box>
            </Grid>
          </Grid>
          {/* navigation small screens */}
          <Grid
            item
            xs={1}>
            <Box
              sx={(theme) => ({
                display: "none",
                [theme.breakpoints.down("md")]: {
                  display: "inline",
                },
              })}>
              <IconButton
                size="medium"
                aria-label="menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit">
                <Tooltip title="Menü">
                  <MenuIcon
                    sx={(theme) => ({
                      color: theme.palette.primary.main,
                    })}
                  />
                </Tooltip>
              </IconButton>
              <StyledMenu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { md: "block", lg: "none" },
                }}>
                {pages.map((page) => (
                  <MenuItem key={page.name}>
                    <StyledNavButton
                      component={NavLink}
                      onClick={handleCloseNavMenu}
                      to={page.route}>
                      {page.name}
                    </StyledNavButton>
                  </MenuItem>
                ))}
                <MenuItem key={colorMode}>{colorModeButton()}</MenuItem>
              </StyledMenu>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppBar>
  );
});
export default Header;
